"use client";

import { useState, useEffect } from "react";
import { useUser } from "@/lib/context/user-context";
import { useUsage } from "@/contexts/usage-context";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenuButton } from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  User, 
  Settings, 
  LogOut, 
  CreditCard,
  HelpCircle,
  ChevronDown
} from "lucide-react";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import { UpgradePromptDialog } from "@/components/ui/upgrade-prompt-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useI18n } from "@/lib/i18n";

export function UserDropdown() {
  const { t, lang } = useI18n() as any;
  const { user, logout } = useUser();
  const { usageInfo, loading, refreshUsage } = useUsage();
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Refresh usage info when dropdown opens
  useEffect(() => {
    if (dropdownOpen) {
      refreshUsage();
    }
  }, [dropdownOpen, refreshUsage]);

  // 如果加载完成但 planType 为 null，说明数据可能未正确获取，重新刷新
  useEffect(() => {
    if (user && !loading && usageInfo.planType === null) {
      console.log('🔄 UserDropdown - planType is null, refreshing usage');
      refreshUsage();
    }
  }, [user, loading, usageInfo.planType, refreshUsage]);

  if (!user) {
    return null;
  }

  // Get user initials for avatar fallback
  const getInitials = (name?: string | null, email?: string | null) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return 'U';
  };

  const userEmail = user.email || '';
  const userName = user.user_metadata?.full_name || user.user_metadata?.name || '';
  const userAvatar = user.user_metadata?.avatar_url || user.user_metadata?.picture || '';
  const initials = getInitials(userName, userEmail);

  // Calculate remaining transcript minutes quote (same logic as TranscriptSection)
  const transcriptMinutesRemaining = usageInfo?.transcriptMinutes?.remaining ?? null;
  const transcriptMinutesLimit = usageInfo?.transcriptMinutes?.limit ?? null;
  const hasUnlimitedQuota = usageInfo?.isPremium && (transcriptMinutesLimit ?? 0) < 0;
  const shouldShowTranscriptQuota = transcriptMinutesRemaining !== null && !hasUnlimitedQuota;

  return (
    <>
    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2 p-2 rounded-lg hover:bg-accent hover:text-accent-foreground w-full group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-2"
          onMouseEnter={() => {
            // Pre-fetch usage info on hover for better perceived performance
            if (!loading && !dropdownOpen) {
              refreshUsage();
            }
          }}
        >
          <Avatar className="h-6 w-6">
            <AvatarImage src={userAvatar} alt={userName || userEmail} />
            <AvatarFallback className="bg-primary text-primary-foreground font-semibold text-xs">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col items-start text-left group-data-[collapsible=icon]:hidden">
            <span className="text-sm font-medium truncate max-w-[150px]">
              {userName || userEmail.split('@')[0]}
            </span>
            {loading ? (
              <Skeleton className="h-3 w-14 mt-1" />
            ) : usageInfo.planType !== null ? (
              <span className="text-xs text-muted-foreground">
                <span className={lang === 'chinese' ? 'font-normal' : ''}>
                  {usageInfo.planType === 'premium'
                    ? t('user.plan.premium')
                    : usageInfo.planType === 'basic'
                    ? (usageInfo.planName || 'Basic Plan')
                    : usageInfo.planType === 'basic_lite'
                    ? (usageInfo.planName || 'Basic Plan Lite')
                    : t('user.plan.free')}
                </span>
              </span>
            ) : null}
          </div>
          <ChevronDown className="h-4 w-4 text-muted-foreground group-data-[collapsible=icon]:hidden ml-auto" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="w-[280px] mx-1" 
        align="end" 
        forceMount
      >
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-2">
            <div className="flex items-center gap-2">
              <Avatar className="h-12 w-12">
                <AvatarImage src={userAvatar} alt={userName || userEmail} />
                <AvatarFallback className="bg-primary/10 text-primary font-semibold text-lg">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <p className="text-sm font-medium">{userName || 'User'}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {userEmail}
                </p>
              </div>
            </div>
            
            {/* Usage Info Banner with Skeleton Loading */}
            {loading ? (
              <div className="rounded-xl bg-muted/50 border p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-5 w-24 rounded-full" />
                </div>
                <Skeleton className="h-3 w-32" />
              </div>
            ) : usageInfo.planType === 'free' ? (
              <div className="rounded-xl bg-muted/50 border p-3 space-y-1 animate-in fade-in-0 duration-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium">
                    <span className={lang === 'chinese' ? 'font-normal' : ''}>{t('user.plan.free')}</span>
                  </span>
                  <Badge variant="secondary" className="text-xs">
                    {usageInfo.remainingTranscripts}/{usageInfo.transcriptLimit} {t('user.usage.transcriptsLabel')}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {usageInfo.remainingTranscripts > 0
                    ? (
                      lang === 'chinese'
                        ? t('user.usage.remaining').replace('{remaining}', String(usageInfo.remainingTranscripts))
                        : `${usageInfo.remainingTranscripts} transcript${usageInfo.remainingTranscripts > 1 ? 's' : ''} ${t('user.usage.remaining')}`
                    )
                    : t('user.usage.limitReached')}
                </p>
                {/* {shouldShowTranscriptQuota && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('transcript.dialog.remaining', 'Remaining')}: {transcriptMinutesRemaining} {t('common.time.minutes', 'min')}
                    {transcriptMinutesLimit !== null && transcriptMinutesLimit >= 0 && (
                      <span className="text-muted-foreground/70"> / {transcriptMinutesLimit}</span>
                    )}
                  </p>
                )} */}
              </div>
            ) : usageInfo.planType === 'basic' ? (
              <div className="rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 p-3 space-y-1 animate-in fade-in-0 duration-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-foreground">
                    {usageInfo.planName || 'Basic Plan'}
                  </span>
                  <Badge variant="secondary" className="text-xs bg-blue-100 dark:bg-blue-900/50 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300">
                    Basic
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {user?.user_metadata?.billingCycle === 'yearly' ? 'Annual subscription' : 'Monthly subscription'}
                </p>
                {shouldShowTranscriptQuota && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('transcript.dialog.remaining', 'Remaining')}: {transcriptMinutesRemaining} {t('common.time.minutes', 'min')}
                    {transcriptMinutesLimit !== null && transcriptMinutesLimit >= 0 && (
                      <span className="text-muted-foreground/70"> / {transcriptMinutesLimit}</span>
                    )}
                  </p>
                )}
              </div>
            ) : usageInfo.planType === 'basic_lite' ? (
              <div className="rounded-xl bg-secondary/70 border border-border p-3 space-y-1 animate-in fade-in-0 duration-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-foreground">
                    {usageInfo.planName || 'Basic Plan Lite'}
                  </span>
                  <Badge variant="secondary" className="text-xs bg-primary/10 border-primary/20 text-primary">
                    Content Access
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Access to episodes with available content
                </p>
                {shouldShowTranscriptQuota && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('transcript.dialog.remaining', 'Remaining')}: {transcriptMinutesRemaining} {t('common.time.minutes', 'min')}
                    {transcriptMinutesLimit !== null && transcriptMinutesLimit >= 0 && (
                      <span className="text-muted-foreground/70"> / {transcriptMinutesLimit}</span>
                    )}
                  </p>
                )}
              </div>
            ) : usageInfo.planType === 'premium' ? (
              <div className="rounded-xl bg-primary border border-yellow-500/20 p-3 animate-in fade-in-0 duration-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-white">{t('user.badge.premiumMember')}</span>
                  {transcriptMinutesRemaining !== null && !hasUnlimitedQuota ? (
                    <Badge className="bg-foreground text-white border-0">
                      {transcriptMinutesRemaining} {t('common.time.minutes', 'min')}
                      {transcriptMinutesLimit !== null && transcriptMinutesLimit >= 0 && (
                        <span className="text-white/80"> / {transcriptMinutesLimit}</span>
                      )}
                    </Badge>
                  ) : (
                    <Badge className="bg-foreground text-white border-0">
                      <span className={lang === 'chinese' ? 'font-normal' : ''}>{t('user.badge.unlimited')}</span>
                    </Badge>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </DropdownMenuLabel>
        
        <DropdownMenuSeparator />
        
        {/* Upgrade Plan - only show for free, basic, and basic_lite users, with loading state */}
        {loading ? (
          <>
            <DropdownMenuItem disabled className="opacity-50">
              <Skeleton className="h-4 w-4 mr-2" />
              <Skeleton className="h-4 w-32" />
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : (usageInfo.planType === 'free' || usageInfo.planType === 'basic' || usageInfo.planType === 'basic_lite') ? (
          <>
            <DropdownMenuItem 
              onClick={(e) => {
                e.preventDefault();
                setDropdownOpen(false); // Close dropdown first
                setShowUpgradeDialog(true);
              }}
              className="cursor-pointer"
            >
              <CreditCard className="mr-2 h-4 w-4" />
              <span className={lang === 'chinese' ? 'font-normal' : ''}>{t('user.actions.upgrade')}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}
        
        <DropdownMenuItem asChild>
          <Link href="/settings" className="cursor-pointer">
            <Settings className="mr-2 h-4 w-4" />
            <span className={lang === 'chinese' ? 'font-normal' : ''}>{t('user.actions.settings')}</span>
          </Link>
        </DropdownMenuItem>
        
        <DropdownMenuItem asChild>
          <Link href="/" className="cursor-pointer">
            <HelpCircle className="mr-2 h-4 w-4" />
            <span className={lang === 'chinese' ? 'font-normal' : ''}>{t('user.actions.help')}</span>
          </Link>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-600 dark:text-red-400">
          <LogOut className="mr-2 h-4 w-4" />
          <span className={lang === 'chinese' ? 'font-normal' : ''}>{t('user.actions.logout')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    {/* Upgrade Prompt Dialog */}
    <UpgradePromptDialog
      isOpen={showUpgradeDialog}
      onClose={() => setShowUpgradeDialog(false)}
      errorType="limit_exceeded"
      remainingCount={usageInfo.remainingTranscripts}
      limitCount={usageInfo.transcriptLimit}
      errorMessage={
        usageInfo.remainingTranscripts === 0 
          ? t('user.dialog.limitReached')
          : (
              lang === 'chinese'
                ? t('user.dialog.remaining').replace('{remaining}', String(usageInfo.remainingTranscripts))
                : t('user.dialog.remaining').replace('{remaining}', String(usageInfo.remainingTranscripts))
            )
      }
    />
    </>
  );
}
